import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox';
import { Check, Minus } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn';

interface CheckboxRootProps extends React.ComponentPropsWithoutRef<typeof BaseCheckbox.Root> {
  className?: string;
}

interface CheckboxIndicatorProps extends Omit<
  React.ComponentPropsWithoutRef<typeof BaseCheckbox.Indicator>,
  'children'
> {
  className?: string;
  /** Replaces the default icons: a checkmark when checked, a minus when indeterminate. */
  children?: React.ReactNode;
}

/** Styling hooks: [data-checked], [data-unchecked], [data-indeterminate], [data-disabled]. */
function Checkbox({ className, disabled, ...props }: CheckboxRootProps) {
  return (
    <BaseCheckbox.Root
      disabled={disabled}
      className={cn(
        'inline-flex items-center justify-center',
        'align-middle',
        'size-5 cursor-pointer rounded-sm',
        'border-input border',
        'transition-colors',
        // Skipped when disabled: it conflicts in specificity with the disabled styles.
        !disabled &&
          'data-checked:border-primary data-checked:bg-primary data-checked:text-primary-foreground',
        !disabled &&
          'data-indeterminate:border-primary data-indeterminate:bg-primary data-indeterminate:text-primary-foreground',
        'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        'data-disabled:border-border-disabled data-disabled:bg-disabled data-disabled:text-text-disabled data-disabled:cursor-not-allowed',
        className,
      )}
      {...props}
    />
  );
}

function CheckboxIndicator({ className, children, ...props }: CheckboxIndicatorProps) {
  const renderIndicator = React.useCallback(
    (renderProps: React.HTMLAttributes<HTMLSpanElement>, state: { indeterminate: boolean }) => (
      <span {...renderProps}>
        {children !== undefined ? (
          children
        ) : state.indeterminate ? (
          <Minus className="size-3" strokeWidth={3} aria-hidden="true" />
        ) : (
          <Check className="size-3" strokeWidth={3} aria-hidden="true" />
        )}
      </span>
    ),
    [children],
  );

  return (
    <BaseCheckbox.Indicator
      className={cn('flex items-center justify-center', 'size-full', className)}
      render={renderIndicator}
      {...props}
    />
  );
}

export { Checkbox, CheckboxIndicator };
